import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Chess, Square } from "chess.js";
import { useDevice } from "../devices/DeviceProvider";
import {
  parseBoardMessage,
  createAnalysisSetupPacket,
  createLiveGameSetupPacket,
} from "./chessProtocol";
import { LastMove, PromotionMove } from "./analysis/types";

export interface PhysicalBoardSyncOptions {
  channelId: string;
  chess: Chess;
  fen: string;
  mode: "analysis" | "game";
  playerColor?: "white" | "black";
  isEditMode?: boolean;
  isGameOver?: boolean;
  disabled?: boolean;
  onMoveExecuted?: (move: { from: Square; to: Square; promotion?: string }, newFen: string) => void;
  onPromotionNeeded?: (promotion: PromotionMove) => void;
}

export function usePhysicalBoardSync({
  channelId,
  chess,
  fen,
  mode,
  playerColor = "white",
  isEditMode = false,
  isGameOver = false,
  disabled = false,
  onMoveExecuted,
  onPromotionNeeded,
}: PhysicalBoardSyncOptions) {
  const { connected, send, onReceive } = useDevice();

  // Pending castle from physical board
  const pendingCastleRef = useRef<{ from: Square; to: Square } | null>(null);

  // Highlighting states
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [optionSquares, setOptionSquares] = useState<Record<string, React.CSSProperties>>({});
  const [lastMove, setLastMove] = useState<LastMove | null>(null);

  // Compute King square in check
  const checkSquare = useMemo<Square | null>(() => {
    if (isEditMode) return null;
    if (!chess.isCheck()) return null;

    const currentTurn = chess.turn();
    const board = chess.board();
    for (const row of board) {
      for (const sq of row) {
        if (sq && sq.type === "k" && sq.color === currentTurn) {
          return sq.square;
        }
      }
    }
    return null;
  }, [chess, fen, isEditMode]);

  // Combined custom square styles for react-chessboard
  const customSquareStyles = useMemo<Record<string, React.CSSProperties>>(() => {
    if (isEditMode) return {};

    const styles: Record<string, React.CSSProperties> = { ...optionSquares };

    // Selected square
    if (selectedSquare) {
      styles[selectedSquare] = {
        backgroundColor: "rgba(255, 215, 0, 0.4)",
      };
    }

    // Last move highlight
    if (lastMove) {
      styles[lastMove.from] = {
        ...(styles[lastMove.from] || {}),
        backgroundColor: "rgba(255, 235, 59, 0.35)",
      };
      styles[lastMove.to] = {
        ...(styles[lastMove.to] || {}),
        backgroundColor: "rgba(255, 235, 59, 0.45)",
      };
    }

    // Check indicator
    if (checkSquare) {
      styles[checkSquare] = {
        background:
          "radial-gradient(ellipse at center, rgba(239, 68, 68, 0.85) 0%, rgba(220, 38, 38, 0.5) 45%, transparent 75%)",
        borderRadius: "12%",
      };
    }

    return styles;
  }, [isEditMode, optionSquares, selectedSquare, lastMove, checkSquare]);

  // Helper to send setup packet to ESP32 board
  const sendBoardSetup = useCallback(
    (
      targetFen: string = fen,
      overrideEditMode: boolean = isEditMode,
      currentLastMove: LastMove | null = lastMove
    ) => {
      let packet: string;
      if (mode === "analysis") {
        packet = createAnalysisSetupPacket(
          targetFen,
          overrideEditMode,
          !overrideEditMode ? chess : undefined,
          currentLastMove
        );
      } else {
        packet = createLiveGameSetupPacket(
          targetFen,
          playerColor,
          chess,
          currentLastMove,
          isGameOver
        );
      }
      console.log(`[Board Communication] Sent packet: ${packet}`);
      send(packet);
    },
    [fen, isEditMode, lastMove, mode, chess, playerColor, isGameOver, send]
  );

  const sendBoardSetupRef = useRef(sendBoardSetup);
  sendBoardSetupRef.current = sendBoardSetup;

  // Sync board on initial connect / mount and periodically every 5 seconds
  useEffect(() => {
    if (!connected || disabled) return;

    // Send immediately on connect / state change
    sendBoardSetup(fen, isEditMode, lastMove);

    // Resend setup packet every 5 seconds when connected to the board
    const interval = setInterval(() => {
      sendBoardSetupRef.current();
    }, 5000);

    return () => clearInterval(interval);
  }, [connected, disabled, sendBoardSetup, fen, isEditMode, lastMove]);

  // Calculate move options and highlight squares
  const getMoveOptions = useCallback(
    (square: Square): boolean => {
      const moves = chess.moves({ square, verbose: true });
      if (moves.length === 0) {
        setOptionSquares({});
        return false;
      }

      const newSquares: Record<string, React.CSSProperties> = {};
      moves.forEach((move) => {
        newSquares[move.to] = {
          background: move.captured
            ? "radial-gradient(circle, rgba(239, 68, 68, 0.75) 28%, transparent 29%)"
            : "radial-gradient(circle, rgba(255, 255, 255, 0.5) 22%, transparent 25%)",
          borderRadius: "50%",
          cursor: "pointer",
        };
      });

      newSquares[square] = {
        backgroundColor: "rgba(255, 215, 0, 0.4)",
      };

      setOptionSquares(newSquares);
      return true;
    },
    [chess]
  );

  const clearSelection = useCallback(() => {
    setSelectedSquare(null);
    setOptionSquares({});
  }, []);

  // Listen for board messages over BLE
  useEffect(() => {
    if (disabled) return;

    const handleBoardData = (msg: string) => {
      const packet = parseBoardMessage(msg);
      if (!packet) return;
      console.log(`[Board Communication] Received packet: ${msg}`, packet);

      if (isEditMode) return;

      switch (packet.type) {
        case "PICKED":
          if (isGameOver || chess.isGameOver()) return;
          if (packet.square) {
            setSelectedSquare(packet.square as Square);
            getMoveOptions(packet.square as Square);
          }
          break;

        case "NO_CHANGE":
          clearSelection();
          break;

        case "CAPTURE_IN_PROGRESS":
          if (isGameOver || chess.isGameOver()) return;
          if (packet.from && packet.to) {
            setSelectedSquare(packet.from as Square);
            setOptionSquares({
              [packet.from]: { backgroundColor: "rgba(0, 200, 255, 0.4)" },
              [packet.to]: {
                background:
                  "radial-gradient(circle, rgba(239, 68, 68, 0.75) 28%, transparent 29%)",
                borderRadius: "50%",
              },
            });
          }
          break;

        case "CASTLE_IN_PROGRESS":
          if (isGameOver || chess.isGameOver()) return;
          if (packet.kingFrom && packet.kingTo) {
            pendingCastleRef.current = {
              from: packet.kingFrom as Square,
              to: packet.kingTo as Square,
            };
          }
          break;

        case "CASTLE_FINISHED":
          if (isGameOver || chess.isGameOver()) {
            pendingCastleRef.current = null;
            clearSelection();
            return;
          }
          if (pendingCastleRef.current) {
            const { from, to } = pendingCastleRef.current;
            pendingCastleRef.current = null;
            try {
              const move = chess.move({ from, to });
              if (move) {
                const newFen = chess.fen();
                const newLastMove: LastMove = { from, to };
                setLastMove(newLastMove);
                clearSelection();
                sendBoardSetup(newFen, false, newLastMove);
                onMoveExecuted?.({ from, to }, newFen);
              }
            } catch (err) {
              console.error("Board castle error:", err);
            }
          }
          break;

        case "MOVED":
        case "CAPTURED":
          if (isGameOver || chess.isGameOver()) {
            clearSelection();
            return;
          }
          if (packet.from && packet.to) {
            const from = packet.from as Square;
            const to = packet.to as Square;

            const piece = chess.get(from);
            if (
              piece &&
              piece.type === "p" &&
              ((piece.color === "w" && to.endsWith("8")) ||
                (piece.color === "b" && to.endsWith("1")))
            ) {
              onPromotionNeeded?.({ from, to });
              clearSelection();
              return;
            }

            try {
              const move = chess.move({ from, to });
              if (move) {
                const newFen = chess.fen();
                const newLastMove: LastMove = { from, to };
                setLastMove(newLastMove);
                clearSelection();
                sendBoardSetup(newFen, false, newLastMove);
                onMoveExecuted?.({ from, to }, newFen);
              }
            } catch (err) {
              console.error("Board move error:", err);
            }
          }
          break;

        case "PROMOTING":
          if (isGameOver || chess.isGameOver()) return;
          if (packet.from && packet.to) {
            onPromotionNeeded?.({
              from: packet.from as Square,
              to: packet.to as Square,
            });
            clearSelection();
          }
          break;

        case "INVALID": {
          const invalidStyles: Record<string, React.CSSProperties> = {};
          packet.pickedPieces?.forEach((sq) => {
            invalidStyles[sq] = { backgroundColor: "rgba(250, 0, 0, 0.5)" };
          });
          packet.placedPieces?.forEach((sq) => {
            invalidStyles[sq] = { backgroundColor: "rgba(250, 80, 10, 0.5)" };
          });
          setOptionSquares(invalidStyles);
          break;
        }
      }
    };

    onReceive(channelId, handleBoardData);
    return () => {
      onReceive(channelId, null);
    };
  }, [
    channelId,
    chess,
    disabled,
    isEditMode,
    getMoveOptions,
    clearSelection,
    onReceive,
    sendBoardSetup,
    onMoveExecuted,
    onPromotionNeeded,
  ]);

  return {
    connected,
    selectedSquare,
    setSelectedSquare,
    optionSquares,
    setOptionSquares,
    lastMove,
    setLastMove,
    checkSquare,
    customSquareStyles,
    sendBoardSetup,
    getMoveOptions,
    clearSelection,
  };
}
